/**
 * hero-slider.js
 * CWV-optimized Swiper initialization for the Hero Slider section.
 *
 * Key CWV strategies:
 *  1. Swiper is initialized lazily via IntersectionObserver — does not block
 *     main thread during initial page parse/render.
 *  2. autoplay is paused when the slider scrolls out of viewport (saves CPU/battery).
 *  3. Images on non-active slides are lazy-loaded using a custom data-src handler.
 *  4. Respects prefers-reduced-motion for autoplay.
 *  5. Banner impression + click analytics hooks are included (dataLayer-ready).
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Config                                                               */
  /* ------------------------------------------------------------------ */
  const AUTOPLAY_DELAY    = 4000;   // ms between slides
  const TRANSITION_SPEED  = 600;    // ms slide transition
  const LAZY_THRESHOLD    = 0.1;    // 10% of slider visible → init
  const PREFETCH_AHEAD    = 1;      // slides to preload ahead of active

  /* ------------------------------------------------------------------ */
  /* Respect user preference for reduced motion                          */
  /* ------------------------------------------------------------------ */
  const prefersReducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  /* ------------------------------------------------------------------ */
  /* Lazy-load images using data-src                                     */
  /* Prevents off-screen images from blocking LCP                        */
  /* ------------------------------------------------------------------ */
  function loadSlideLazyImages(slideEl) {
    const imgs = slideEl.querySelectorAll('img[data-src]');
    imgs.forEach(function (img) {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Analytics helpers (dataLayer / GTM compatible)                      */
  /* ------------------------------------------------------------------ */
  function pushBannerImpression(slide, position) {
    const anchor = slide.querySelector('[data-banner-name]');
    if (!anchor) return;
    const bannerName = anchor.dataset.bannerName || '';
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'banner_view',
        banner_name: bannerName,
        banner_position: position,
      });
    }
  }

  function bindBannerClicks(swiperEl) {
    swiperEl.addEventListener('click', function (e) {
      const anchor = e.target.closest('[data-banner-name]');
      if (!anchor) return;
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'banner_click',
          banner_name: anchor.dataset.bannerName || '',
          banner_position: anchor.dataset.bannerPosition || '',
        });
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Core init                                                           */
  /* ------------------------------------------------------------------ */
  function initHeroSwiper(swiperEl) {
    // Load the first slide's lazy images immediately (LCP candidate)
    const firstSlide = swiperEl.querySelector('.swiper-slide');
    if (firstSlide) loadSlideLazyImages(firstSlide);

    const swiper = new Swiper(swiperEl, {
      loop: true,
      speed: prefersReducedMotion ? 0 : TRANSITION_SPEED,
      a11y: {
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
      },

      /* Autoplay — disabled for reduced motion */
      autoplay: prefersReducedMotion
        ? false
        : {
            delay: AUTOPLAY_DELAY,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          },

      /* Pagination */
      pagination: {
        el: swiperEl.querySelector('.swiper-pagination-hero'),
        clickable: true,
        bulletElement: 'button',        // semantic + keyboard-accessible
        renderBullet: function (index, className) {
          return '<button class="' + className + '" aria-label="Go to slide ' + (index + 1) + '"></button>';
        },
      },

      /* Keyboard navigation */
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },

      /* Touch / swipe */
      grabCursor: true,
      touchRatio: 1,
      threshold: 10,              // px — prevents accidental swipe on scroll

      /* Lazy load non-active slides */
      on: {
        slideChange: function () {
          const activeSlide  = this.slides[this.activeIndex];
          const prevSlide    = this.slides[this.activeIndex - PREFETCH_AHEAD] || null;
          const nextSlide    = this.slides[this.activeIndex + PREFETCH_AHEAD] || null;

          [activeSlide, prevSlide, nextSlide].forEach(function (slide) {
            if (slide) loadSlideLazyImages(slide);
          });

          pushBannerImpression(activeSlide, this.realIndex + 1);
        },
        afterInit: function () {
          /* Fire impression for the first slide */
          const activeSlide = this.slides[this.activeIndex];
          if (activeSlide) pushBannerImpression(activeSlide, 1);
        },
      },
    });

    /* ---------------------------------------------------------------- */
    /* Pause autoplay when slider is not visible — saves CPU & battery   */
    /* ---------------------------------------------------------------- */
    if ('IntersectionObserver' in window && !prefersReducedMotion) {
      const visibilityObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!swiper.autoplay) return;
            if (entry.isIntersecting) {
              swiper.autoplay.start();
            } else {
              swiper.autoplay.stop();
            }
          });
        },
        { threshold: 0.2 }
      );
      visibilityObserver.observe(swiperEl);
    }

    /* Analytics click binding */
    bindBannerClicks(swiperEl);

    return swiper;
  }

  /* ------------------------------------------------------------------ */
  /* Lazy init via IntersectionObserver                                  */
  /* Swiper JS itself is heavy; defer init until slider enters viewport. */
  /* ------------------------------------------------------------------ */
  function observeAndInit(swiperEl) {
    if (!('IntersectionObserver' in window)) {
      // Fallback: init immediately for old browsers
      initHeroSwiper(swiperEl);
      return;
    }

    /* If already in viewport (above-the-fold hero), init immediately */
    const rect = swiperEl.getBoundingClientRect();
    if (rect.top < window.innerHeight) {
      initHeroSwiper(swiperEl);
      return;
    }

    const initObserver = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            observer.disconnect();
            initHeroSwiper(entry.target);
          }
        });
      },
      { threshold: LAZY_THRESHOLD }
    );

    initObserver.observe(swiperEl);
  }

  /* ------------------------------------------------------------------ */
  /* Boot — wait for DOM, then wire up all hero sliders on the page      */
  /* ------------------------------------------------------------------ */
  function boot() {
    const sliders = document.querySelectorAll('.hero-swiper');
    if (!sliders.length) return;

    /*
     * Guard against script-order race conditions.
     * On some templates (e.g. collection pages where this section
     * renders mid-document), this deferred script can execute before
     * the global swiper-bundle.min.js script tag near </body> does.
     * Deferred scripts run in source order, so if Swiper hasn't
     * attached to window yet, poll briefly instead of failing silently.
     */
    if (typeof window.Swiper === 'undefined') {
      let attempts = 0;
      const maxAttempts = 50; // 50 × 20ms = up to 1s wait
      const waitForSwiper = setInterval(function () {
        attempts++;
        if (typeof window.Swiper !== 'undefined') {
          clearInterval(waitForSwiper);
          sliders.forEach(observeAndInit);
        } else if (attempts >= maxAttempts) {
          clearInterval(waitForSwiper);
          console.error('hero-slider.js: Swiper library failed to load — slider will not initialize.');
        }
      }, 20);
      return;
    }

    sliders.forEach(observeAndInit);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify section re-render support (Theme Editor live preview) */
  document.addEventListener('shopify:section:load', function (e) {
    const swiperEl = e.target.querySelector('.hero-swiper');
    if (swiperEl) initHeroSwiper(swiperEl);
  });

})();